<template>
  <div v-if="isOpen" class="fixed inset-0 z-[200] flex items-start justify-center px-4 py-4 sm:py-8 overflow-y-auto bg-black/50 backdrop-blur-sm" @click.self="closeModal">
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-2xl my-2 sm:my-4 max-h-[calc(100dvh-2rem)] flex flex-col overflow-hidden transform transition-all animate-in fade-in zoom-in duration-300 text-left relative">
      <!-- Modal Header: Matches Sidebar Color -->
      <div class="px-4 sm:px-6 py-4 bg-[#0d4169] text-white flex items-center justify-between rounded-t-xl shrink-0">
        <h2 class="text-lg font-bold m-0">{{ editUser ? 'Edit User' : 'Add New User' }}</h2>
        <button @click="closeModal" class="p-1 hover:bg-white/10 rounded-full transition-colors text-white">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>

      <!-- Modal Body -->
      <form @submit.prevent="handleSubmit" class="flex flex-col flex-1 min-h-0 overflow-hidden">
        <div class="p-4 sm:p-8 overflow-y-auto flex-1">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          <!-- Full Name -->
          <div class="flex flex-col gap-1.5">
            <label class="text-[0.85rem] font-semibold text-[#1e293b]">Full Name</label>
            <input 
              v-model="form.full_name"
              type="text" 
              placeholder="Enter full name" 
              class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm"
              required
            >
          </div>

          <!-- Phone Number -->
          <div class="flex flex-col gap-1.5">
            <label class="text-[0.85rem] font-semibold text-[#1e293b]">Phone Number</label>
            <input 
              v-model="form.phone_number"
              type="tel" 
              placeholder="Enter phone number" 
              class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm"
              required
            >
          </div>

          <div class="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4">
            <!-- Sex -->
            <div class="flex flex-col gap-1.5">
              <label class="text-[0.85rem] font-semibold text-[#1e293b]">Sex</label>
              <select 
                v-model="form.sex"
                class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm bg-white cursor-pointer"
                required
              >
                <option value="" disabled>Select Sex</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>

            <!-- Username -->
            <div class="flex flex-col gap-1.5">
              <label class="text-[0.85rem] font-semibold text-[#1e293b]">Username</label>
              <input 
                v-model="form.username"
                type="text" 
                placeholder="Enter username" 
                class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm"
                required
              >
            </div>

            <!-- Salary -->
            <div class="flex flex-col gap-1.5">
              <label class="text-[0.85rem] font-semibold text-[#1e293b]">Salary</label>
              <input
                v-model.number="form.salary"
                type="number"
                min="0"
                step="0.01"
                placeholder="Enter salary"
                class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#2563eb] transition-all text-sm"
              >
            </div>
          </div>

          <div class="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4">
            <!-- Department (multi-select, options from Department) -->
            <div class="flex flex-col gap-1.5 relative dropdown-container">
              <label class="text-[0.85rem] font-semibold text-[#1e293b]">Department</label>
              <div
                ref="departmentDropdownTrigger"
                @click.stop="toggleDropdown('departments')"
                class="px-3 py-2 border border-[#cbd5e1] rounded-md bg-white flex items-center justify-between cursor-pointer text-sm min-h-[40px] hover:border-slate-400 transition-colors"
                :class="activeDropdown === 'departments' ? 'border-[#2563eb] ring-1 ring-[#2563eb]' : ''"
              >
                <span v-if="selectedDepartmentIds.length === 0" class="text-slate-400">Select Department</span>
                <span v-else class="text-[#1e293b] truncate">
                  {{ selectedDepartmentNames.join(', ') }}
                </span>
                <svg
                  class="w-4 h-4 text-slate-400 transition-transform"
                  :class="activeDropdown === 'departments' ? 'rotate-180' : ''"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              <Teleport to="body">
                <div
                  v-if="activeDropdown === 'departments'"
                  class="department-dropdown-panel fixed z-[400] bg-white border border-slate-200 shadow-xl rounded-md overflow-y-auto animate-in fade-in slide-in-from-top-1 duration-200 p-2"
                  :style="{ ...departmentDropdownStyle, maxHeight: departmentDropdownMaxHeight }"
                  @click.stop
                >
                  <div
                    v-for="dept in departments"
                    :key="dept.id"
                    class="px-3 py-2 hover:bg-slate-50 transition-colors flex items-center justify-between rounded-md border-b border-slate-50 last:border-0"
                  >
                    <span class="text-[0.8rem] font-bold text-[#1e293b] leading-tight">{{ dept.name }}</span>
                    <input
                      type="checkbox"
                      :value="dept.id"
                      v-model="selectedDepartmentIds"
                      class="w-3.5 h-3.5 rounded border-slate-300 text-[#0d4169] focus:ring-[#0d4169] cursor-pointer"
                    />
                  </div>
                  <div v-if="departments.length === 0" class="px-3 py-2 text-[0.8rem] text-slate-400">
                    No departments found
                  </div>
                </div>
              </Teleport>
            </div>

            <!-- Store Category (multi-select, active categories by company/branch scope) -->
            <div class="flex flex-col gap-1.5 relative dropdown-container">
              <label class="text-[0.85rem] font-semibold text-[#1e293b]">Store Category</label>
              <div
                ref="categoryDropdownTrigger"
                @click.stop="toggleDropdown('categories')"
                class="px-3 py-2 border border-[#cbd5e1] rounded-md bg-white flex items-center justify-between cursor-pointer text-sm min-h-[40px] hover:border-slate-400 transition-colors"
                :class="activeDropdown === 'categories' ? 'border-[#2563eb] ring-1 ring-[#2563eb]' : ''"
              >
                <span v-if="selectedCategoryIds.length === 0" class="text-slate-400">Select Category</span>
                <span v-else class="text-[#1e293b] truncate">
                  {{ selectedCategoryNames.join(', ') }}
                </span>
                <svg
                  class="w-4 h-4 text-slate-400 transition-transform"
                  :class="activeDropdown === 'categories' ? 'rotate-180' : ''"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              <Teleport to="body">
                <div
                  v-if="activeDropdown === 'categories'"
                  class="category-dropdown-panel fixed z-[400] bg-white border border-slate-200 shadow-xl rounded-md overflow-y-auto animate-in fade-in slide-in-from-top-1 duration-200 p-2"
                  :style="{ ...categoryDropdownStyle, maxHeight: categoryDropdownMaxHeight }"
                  @click.stop
                >
                  <div
                    v-for="cat in categories"
                    :key="cat.id"
                    class="px-3 py-2 hover:bg-slate-50 transition-colors flex items-center justify-between rounded-md border-b border-slate-50 last:border-0"
                  >
                    <span class="text-[0.8rem] font-bold text-[#1e293b] leading-tight">{{ cat.name }}</span>
                    <input
                      type="checkbox"
                      :value="cat.id"
                      v-model="selectedCategoryIds"
                      class="w-3.5 h-3.5 rounded border-slate-300 text-[#0d4169] focus:ring-[#0d4169] cursor-pointer"
                    />
                  </div>
                  <div v-if="categories.length === 0" class="px-3 py-2 text-[0.8rem] text-slate-400">
                    No categories found
                  </div>
                </div>
              </Teleport>
            </div>

            <!-- Role (multi-select, options from Role) -->
            <div class="flex flex-col gap-1.5 relative dropdown-container">
              <label class="text-[0.85rem] font-semibold text-[#1e293b]">Role</label>
              <div
                ref="roleDropdownTrigger"
                @click.stop="isAdminPrivilege ? null : toggleDropdown('roles')"
                class="px-3 py-2 border border-[#cbd5e1] rounded-md flex items-center justify-between text-sm min-h-[40px] transition-colors"
                :class="isAdminPrivilege ? 'bg-slate-100 cursor-not-allowed text-slate-500' : 'bg-white cursor-pointer hover:border-slate-400 ' + (activeDropdown === 'roles' ? 'border-[#2563eb] ring-1 ring-[#2563eb]' : '')"
              >
                <span v-if="selectedRoleIds.length === 0" :class="isAdminPrivilege ? 'text-slate-500' : 'text-slate-400'">
                  {{ isAdminPrivilege && props.editUser?.role ? props.editUser.role : 'Select Role' }}
                </span>
                <span v-else class="truncate" :class="isAdminPrivilege ? 'text-slate-500 font-medium' : 'text-[#1e293b]'">
                  {{ selectedRoleNames.join(', ') }}
                </span>
                <svg
                  v-if="!isAdminPrivilege"
                  class="w-4 h-4 text-slate-400 transition-transform"
                  :class="activeDropdown === 'roles' ? 'rotate-180' : ''"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              <Teleport to="body">
                <div
                  v-if="activeDropdown === 'roles'"
                  class="role-dropdown-panel fixed z-[400] bg-white border border-slate-200 shadow-xl rounded-md overflow-y-auto animate-in fade-in slide-in-from-top-1 duration-200 p-2"
                  :style="{ ...roleDropdownStyle, maxHeight: roleDropdownMaxHeight }"
                  @click.stop
                >
                  <div
                    v-for="role in roles"
                    :key="role.id"
                    class="px-3 py-2 hover:bg-slate-50 transition-colors flex items-center justify-between rounded-md border-b border-slate-50 last:border-0"
                  >
                    <span class="text-[0.8rem] font-bold text-[#1e293b] leading-tight">{{ role.name }}</span>
                    <input
                      type="checkbox"
                      :value="role.id"
                      v-model="selectedRoleIds"
                      class="w-3.5 h-3.5 rounded border-slate-300 text-[#0d4169] focus:ring-[#0d4169] cursor-pointer"
                    />
                  </div>
                  <div v-if="roles.length === 0" class="px-3 py-2 text-[0.8rem] text-slate-400">
                    No roles found
                  </div>
                </div>
              </Teleport>
            </div>
          </div>

          <div
            class="md:col-span-2 grid grid-cols-1 gap-4"
            :class="showBranchSelector ? 'md:grid-cols-3' : 'md:grid-cols-2'"
          >
            <!-- Select Branch (multi-select, visible only for company-level users) -->
            <div v-if="showBranchSelector" class="flex flex-col gap-1.5 relative dropdown-container">
              <label class="text-[0.85rem] font-semibold text-[#1e293b]">Select Branch</label>
              <div
                ref="branchDropdownTrigger"
                @click.stop="toggleDropdown('branches')"
                class="px-3 py-2 border border-[#cbd5e1] rounded-md bg-white flex items-center justify-between cursor-pointer text-sm min-h-[40px] hover:border-slate-400 transition-colors"
                :class="activeDropdown === 'branches' ? 'border-[#2563eb] ring-1 ring-[#2563eb]' : ''"
              >
                <span v-if="selectedOrganizationNames.length === 0" class="text-slate-400">Select Branch</span>
                <span v-else class="text-[#1e293b] truncate">{{ selectedOrganizationNames.join(', ') }}</span>
                <svg
                  class="w-4 h-4 text-slate-400 transition-transform"
                  :class="activeDropdown === 'branches' ? 'rotate-180' : ''"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              <Teleport to="body">
                <div
                  v-if="activeDropdown === 'branches'"
                  class="branch-dropdown-panel fixed z-[400] bg-white border border-slate-200 shadow-xl rounded-md overflow-y-auto animate-in fade-in slide-in-from-top-1 duration-200 p-2"
                  :style="{ ...branchDropdownStyle, maxHeight: branchDropdownMaxHeight }"
                  @click.stop
                >
                  <div
                    v-if="currentCompanyName"
                    class="px-3 py-2 bg-slate-50 transition-colors flex items-center justify-between rounded-md border-b border-slate-100"
                  >
                    <span class="text-[0.8rem] font-bold text-[#0d4169] leading-tight">{{ currentCompanyName }} (Head Office)</span>
                    <input
                      type="checkbox"
                      v-model="isCompanySelected"
                      class="w-3.5 h-3.5 rounded border-slate-300 text-[#0d4169] focus:ring-[#0d4169] cursor-pointer"
                    />
                  </div>
                  <div
                    v-for="branch in branches"
                    :key="branch.id"
                    class="px-3 py-2 hover:bg-slate-50 transition-colors flex items-center justify-between rounded-md border-b border-slate-50 last:border-0"
                  >
                    <span class="text-[0.8rem] font-bold text-[#1e293b] leading-tight">{{ branch.name }}</span>
                    <input
                      type="checkbox"
                      :value="branch.id"
                      v-model="selectedBranchIds"
                      class="w-3.5 h-3.5 rounded border-slate-300 text-[#0d4169] focus:ring-[#0d4169] cursor-pointer"
                    />
                  </div>
                  <div v-if="branches.length === 0" class="px-3 py-2 text-[0.8rem] text-slate-400">
                    No branches found
                  </div>
                </div>
              </Teleport>
            </div>

            <!-- Privileges (Multi-select Dropdown) -->
            <div class="flex flex-col gap-1.5 relative dropdown-container">
              <label class="text-[0.85rem] font-semibold text-[#1e293b]">Privilege</label>
              <div 
                @click.stop="isAdminPrivilege ? null : toggleDropdown('privileges')"
                class="px-3 py-2 border border-[#cbd5e1] rounded-md flex items-center justify-between text-sm min-h-[40px] transition-colors"
                :class="isAdminPrivilege ? 'bg-slate-100 cursor-not-allowed text-slate-500' : 'bg-white cursor-pointer hover:border-slate-400 ' + (activeDropdown === 'privileges' ? 'border-[#2563eb] ring-1 ring-[#2563eb]' : '')"
              >
                <span v-if="isAdminPrivilege" class="text-emerald-700 font-bold">All Privileges (System Admin)</span>
                <span v-else-if="getSelectedPrivilegesCount === 0" class="text-slate-400">Select Privileges</span>
                <span v-else class="truncate text-[#1e293b]">
                  {{ getSelectedPrivilegesSummary }}
                </span>
                <svg v-if="!isAdminPrivilege" class="w-4 h-4 text-slate-400 transition-transform" :class="activeDropdown === 'privileges' ? 'rotate-180' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" /></svg>
              </div>
              
              <!-- Dropdown Menu -->
              <Teleport to="body">
                <div v-if="activeDropdown === 'privileges'" class="teleport-dropdown fixed inset-0 z-[300] flex items-center justify-center px-4 overflow-y-auto bg-black/50 backdrop-blur-sm" @click.self="activeDropdown = null">
                  <div class="bg-white rounded-xl shadow-2xl w-full max-w-5xl my-4 transform transition-all animate-in fade-in zoom-in duration-300 text-left overflow-hidden h-[90vh] max-h-[90vh] flex flex-col">
                    <!-- Modal Header -->
                    <div class="px-6 py-4 bg-[#0d4169] text-white flex items-center justify-between rounded-t-xl shrink-0">
                      <h2 class="text-lg font-bold m-0">Select Privileges</h2>
                      <button type="button" @click="activeDropdown = null" class="p-1 hover:bg-white/10 rounded-full transition-colors text-white">
                        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </div>

                    <!-- Modal Body -->
                    <div class="p-6 flex-1 overflow-y-auto bg-white">
                      <table class="min-w-max w-full border-collapse bg-white text-[0.65rem]">
                        <thead class="bg-white border-b-2 border-slate-200">
                          <tr>
                            <th class="py-2 text-sm font-bold text-slate-700 bg-white">#</th>
                            <th class="text-left py-2 text-sm font-bold text-slate-700 bg-white">Privilege Name</th>
                            <th class="py-2 bg-white"></th>
                            <th class="capitalize py-2 text-center bg-white text-slate-600 font-bold text-xs tracking-wider">Create</th>
                            <th class="capitalize py-2 text-center bg-white text-slate-600 font-bold text-xs tracking-wider">Edit</th>
                            <th class="capitalize py-2 text-center bg-white text-slate-600 font-bold text-xs tracking-wider">See</th>
                            <th class="capitalize py-2 text-center bg-white text-slate-600 font-bold text-xs tracking-wider">Void</th>
                            <th class="capitalize py-2 text-center bg-white text-slate-600 font-bold text-xs tracking-wider">Approve</th>
                            
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="priv in availablePrivileges"
                            :key="priv.id"
                            class="transition-colors border-b"
                            :class="[
                              priv.level === 0 ? 'bg-gray-200 border-t-2 border-slate-300' : 'hover:bg-slate-100/50'
                            ]"
                          >
                            <td class="text-center py-2 text-slate-400 text-xs">{{ getPrivilegeDisplayNumber(priv) }}</td>
                            <td class="py-3" :style="{ paddingLeft: priv.level * 1.5 + 'rem' }">
                              <span 
                                class="flex items-center gap-1.5"
                                :class="{
                                  'text-emerald-700 font-bold uppercase tracking-widest text-[0.7rem]': priv.level === 0,
                                  'text-[#0d4169] font-bold text-xs': priv.level === 1 && priv.isParent,
                                  'text-slate-600 font-semibold text-xs': !priv.isParent || priv.level > 1
                                }"
                              >
                                <span v-if="priv.level > 0" class="text-slate-300 font-normal">└</span>
                                {{ priv.label }}
                              </span>
                            </td>
                            <td class="text-center py-2">
                              <div class="inline-flex items-center justify-center gap-3 flex-wrap">
                                <label class="inline-flex items-center cursor-pointer group">
                                  <input 
                                    type="checkbox" 
                                    :checked="isAllChecked(priv.id)"
                                    @change="toggleAll(priv.id)"
                                    class="w-4 h-4 shrink-0 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                                  >
                                  <span class="text-xs font-medium ml-1 group-hover:text-emerald-700">All</span>
                                </label>
                                <label
                                  v-if="isPayrollWorkflowWithEditPrivilege(priv.id)"
                                  class="inline-flex items-center cursor-pointer group"
                                >
                                  <input
                                    type="checkbox"
                                    :checked="privilegeCheckboxChecked(priv.id, 'Edit')"
                                    @change="togglePrivilegeAction(priv.id, 'Edit', $event.target.checked)"
                                    class="w-4 h-4 shrink-0 rounded border-slate-300 text-[#0d4169] focus:ring-[#0d4169] cursor-pointer"
                                  >
                                  <span class="text-xs font-medium ml-1 text-slate-500 group-hover:text-[#0d4169]">Edit</span>
                                </label>
                              </div>
                            </td>
                            <template v-if="isPayrollWorkflowPrivilege(priv.id)">
                              <td colspan="5" class="py-2 privilege-payroll-actions">
                                <div class="privilege-payroll-actions-grid">
                                  <label
                                    v-for="action in PAYROLL_WORKFLOW_ACTIONS"
                                    :key="action"
                                    class="inline-flex items-center group"
                                    :class="isPrivilegeActionLocked(priv.id, action) ? 'cursor-not-allowed' : 'cursor-pointer'"
                                  >
                                    <input
                                      type="checkbox"
                                      :checked="privilegeCheckboxChecked(priv.id, action)"
                                      @change="togglePrivilegeAction(priv.id, action, $event.target.checked)"
                                      :disabled="isPrivilegeActionLocked(priv.id, action)"
                                      class="w-4 h-4 shrink-0 rounded border-slate-300 text-[#0d4169] focus:ring-[#0d4169]"
                                      :class="isPrivilegeActionLocked(priv.id, action) ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'"
                                    >
                                    <span class="text-xs font-medium ml-1 text-slate-500 group-hover:text-[#0d4169]">{{ action }}</span>
                                  </label>
                                </div>
                              </td>
                            </template>
                            <template v-else>
                            <td class="text-center py-2">
                              <label
                                v-if="isPrivilegeColumnVisible(priv.id, 'create')"
                                class="inline-flex items-center group"
                                :class="isPrivilegeActionLocked(priv.id, getPrivilegeTableColumnAction(priv.id, 'create')) ? 'cursor-not-allowed' : 'cursor-pointer'"
                              >
                                <input 
                                  type="checkbox" 
                                  :checked="privilegeCheckboxChecked(priv.id, getPrivilegeTableColumnAction(priv.id, 'create'))"
                                  @change="togglePrivilegeAction(priv.id, getPrivilegeTableColumnAction(priv.id, 'create'), $event.target.checked)"
                                  :disabled="isPrivilegeActionLocked(priv.id, getPrivilegeTableColumnAction(priv.id, 'create'))"
                                  class="w-4 h-4 shrink-0 rounded border-slate-300 text-[#0d4169] focus:ring-[#0d4169]"
                                  :class="isPrivilegeActionLocked(priv.id, getPrivilegeTableColumnAction(priv.id, 'create')) ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'"
                                >
                                <span class="text-xs font-medium ml-1 text-slate-500 group-hover:text-[#0d4169]">{{ getPrivilegeColumnLabel(priv.id, 'create') }}</span>
                              </label>
                            </td>
                            <td class="text-center py-2">
                              <label
                                v-if="isPrivilegeColumnVisible(priv.id, 'edit')"
                                class="inline-flex items-center group"
                                :class="isPrivilegeActionLocked(priv.id, getPrivilegeTableColumnAction(priv.id, 'edit')) ? 'cursor-not-allowed' : 'cursor-pointer'"
                              >
                                <input 
                                  type="checkbox" 
                                  :checked="privilegeCheckboxChecked(priv.id, getPrivilegeTableColumnAction(priv.id, 'edit'))"
                                  @change="togglePrivilegeAction(priv.id, getPrivilegeTableColumnAction(priv.id, 'edit'), $event.target.checked)"
                                  :disabled="isPrivilegeActionLocked(priv.id, getPrivilegeTableColumnAction(priv.id, 'edit'))"
                                  class="w-4 h-4 shrink-0 rounded border-slate-300 text-[#0d4169] focus:ring-[#0d4169]"
                                  :class="isPrivilegeActionLocked(priv.id, getPrivilegeTableColumnAction(priv.id, 'edit')) ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'"
                                >
                                <span class="text-xs font-medium ml-1 text-slate-500 group-hover:text-[#0d4169]">{{ getPrivilegeColumnLabel(priv.id, 'edit') }}</span>
                              </label>
                            </td>
                            <td class="text-center py-2">
                              <label
                                v-if="isPrivilegeColumnVisible(priv.id, 'view')"
                                class="inline-flex items-center group"
                                :class="isPrivilegeActionLocked(priv.id, getPrivilegeTableColumnAction(priv.id, 'view')) ? 'cursor-not-allowed' : 'cursor-pointer'"
                              >
                                <input 
                                  type="checkbox" 
                                  :checked="privilegeCheckboxChecked(priv.id, getPrivilegeTableColumnAction(priv.id, 'view'))"
                                  @change="togglePrivilegeAction(priv.id, getPrivilegeTableColumnAction(priv.id, 'view'), $event.target.checked)"
                                  :disabled="isPrivilegeActionLocked(priv.id, getPrivilegeTableColumnAction(priv.id, 'view'))"
                                  class="w-4 h-4 shrink-0 rounded border-slate-300 text-[#0d4169] focus:ring-[#0d4169]"
                                  :class="isPrivilegeActionLocked(priv.id, getPrivilegeTableColumnAction(priv.id, 'view')) ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'"
                                >
                                <span class="text-xs font-medium ml-1 text-slate-500 group-hover:text-[#0d4169]">{{ getPrivilegeColumnLabel(priv.id, 'view') }}</span>
                              </label>
                            </td>
                            <td class="text-center py-2">
                              <label
                                v-if="isPrivilegeColumnVisible(priv.id, 'void')"
                                class="inline-flex items-center group"
                                :class="isPrivilegeActionLocked(priv.id, getPrivilegeTableColumnAction(priv.id, 'void')) ? 'cursor-not-allowed' : 'cursor-pointer'"
                              >
                                <input 
                                  type="checkbox" 
                                  :checked="privilegeCheckboxChecked(priv.id, getPrivilegeTableColumnAction(priv.id, 'void'))"
                                  @change="togglePrivilegeAction(priv.id, getPrivilegeTableColumnAction(priv.id, 'void'), $event.target.checked)"
                                  :disabled="isPrivilegeActionLocked(priv.id, getPrivilegeTableColumnAction(priv.id, 'void'))"
                                  class="w-4 h-4 shrink-0 rounded border-slate-300 text-[#0d4169] focus:ring-[#0d4169]"
                                  :class="isPrivilegeActionLocked(priv.id, getPrivilegeTableColumnAction(priv.id, 'void')) ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'"
                                >
                                <span class="text-xs font-medium ml-1 text-slate-500 group-hover:text-[#0d4169]">{{ getPrivilegeColumnLabel(priv.id, 'void') }}</span>
                              </label>
                            </td>
                            <td class="text-center py-2">
                              <label
                                v-if="isPrivilegeColumnVisible(priv.id, 'approve')"
                                class="inline-flex items-center group"
                                :class="isPrivilegeActionLocked(priv.id, getPrivilegeTableColumnAction(priv.id, 'approve')) ? 'cursor-not-allowed' : 'cursor-pointer'"
                              >
                                <input 
                                  type="checkbox" 
                                  :checked="privilegeCheckboxChecked(priv.id, getPrivilegeTableColumnAction(priv.id, 'approve'))"
                                  @change="togglePrivilegeAction(priv.id, getPrivilegeTableColumnAction(priv.id, 'approve'), $event.target.checked)"
                                  :disabled="isPrivilegeActionLocked(priv.id, getPrivilegeTableColumnAction(priv.id, 'approve'))"
                                  class="w-4 h-4 shrink-0 rounded border-slate-300 text-[#0d4169] focus:ring-[#0d4169]"
                                  :class="isPrivilegeActionLocked(priv.id, getPrivilegeTableColumnAction(priv.id, 'approve')) ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'"
                                >
                                <span class="text-xs font-medium ml-1 text-slate-500 group-hover:text-[#0d4169]">{{ getPrivilegeColumnLabel(priv.id, 'approve') }}</span>
                              </label>
                            </td>
                            </template>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </Teleport>
            </div>

            <!-- Is Active -->
            <div v-if="1==3" class="flex flex-col justify-center gap-2 py-2">
              <label class="text-[0.85rem] font-semibold text-[#1e293b]">User Status</label>
              <div class="flex items-center gap-4">
                <button 
                  type="button"
                  @click="form.status = form.status === 1 ? 0 : 1"
                  class="relative w-12 h-6 rounded-full transition-colors duration-200 focus:outline-none"
                  :class="form.status === 1 ? 'bg-[#22c55e]' : 'bg-red-500'"
                >
                  <span 
                    class="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform duration-200 shadow-sm"
                    :class="form.status === 1 ? 'translate-x-6' : 'translate-x-0'"
                  ></span>
                </button>
                <span class="text-xs font-bold" :class="form.status === 1 ? 'text-[#166534]' : 'text-red-600'">
                  {{ form.status === 1 ? 'Access ON' : 'Access OFF' }}
                </span>
              </div>
            </div>
          </div>
        </div>
        </div>

        <!-- Submit Buttons: Register Center, Cancel Right -->
        <div class="px-4 sm:px-8 pb-4 sm:pb-8 pt-4 sm:mt-0 sm:pt-6 border-t border-slate-100 flex items-center gap-2 sm:gap-0 shrink-0">
          <div class="hidden sm:block flex-1"></div>
          <div class="flex-1 sm:flex-initial flex justify-center min-w-0">
            <button 
              type="submit"
              :disabled="saving"
              class="whitespace-nowrap px-5 sm:px-10 py-2.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center gap-2 shrink-0"
            >
              <div
                v-if="saving"
                class="h-5 w-5 border-2 border-white/30 border-t-transparent rounded-full animate-spin shrink-0"
              ></div>
              <span v-if="!saving" class="whitespace-nowrap">{{ props.editUser ? 'Update User' : 'Register User' }}</span>
            </button>
          </div>
          <div class="shrink-0 sm:flex-1 flex justify-end">
            <button 
              type="button" 
              @click="closeModal" 
              class="px-4 sm:px-6 py-2.5 text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors whitespace-nowrap"
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { reactive, defineProps, defineEmits, ref, onMounted, onUnmounted, watch, computed, nextTick } from 'vue'
import { apiFetch } from '../../utils/api'
import store from '../../store'

const props = defineProps({
  isOpen: Boolean,
  editUser: { type: Object, default: null }
})

const emit = defineEmits(['close', 'register', 'update'])

const isAdminPrivilege = computed(() => {
  if (!props.editUser || !props.editUser.privilege) return false
  const p = typeof props.editUser.privilege === 'string' 
    ? JSON.parse(props.editUser.privilege) 
    : props.editUser.privilege
  return p && p.all === true
})

const currentUser = computed(() => store.getters.currentUser)
const departments = ref([])
const categories = ref([])
const branches = ref([])
const roles = ref([])
const saving = ref(false)

const availablePrivileges = [
  // ── Top Level ────────────────────────────────────────
  { id: "Dashboard", label: "Dashboard", level: 0, isParent: true },
  { id: "Items Reached Min Stock Level", label: "Items Reached Min Stock Level", level: 1 },
  { id: "Expired and Near Expiry Items", label: "Expired and Near Expiry Items", level: 1 },
  { id: "Weekly Total Orders", label: "Weekly Total Orders", level: 1 },
  { id: "Weekly Total Sales", label: "Weekly Total Sales", level: 1 },
  { id: "Today's Total GRV", label: "Today's Total GRV", level: 1 },
  { id: "Today's Total NS", label: "Today's Total NS", level: 1 },
  { id: "Today's Total SIV", label: "Today's Total SIV", level: 1 },
  
  // ── Users ────────────────────────────────────────────
  { id: "Users", label: "Users", level: 0, isParent: true },

  // ── Register ─────────────────────────────────────────
  { id: "Register Users", label: "Register", level: 0, isParent: true },
  { id: "Branch", label: "Branch", level: 1 },
  { id: "Supplier", label: "Supplier", level: 1 },
  { id: "Department", label: "Department", level: 1 },
  { id: "Stock Category", label: "Stock Category", level: 1 },
  { id: "Roles", label: "Roles", level: 1 },
  { id: "Menu List", label: "Menu List", level: 1 },

  // ── Inventory ────────────────────────────────────────
  { id: "Stock", label: "Stock", level: 0, isParent: true },
  { id: "Item Register", label: "Item Register", level: 1 },
  { id: "GRV", label: "GRV", level: 1 },
  { id: "Store Request", label: "Store Request", level: 1 },
  { id: "SIV (Internal Issue)", label: "SIV (Internal Issue)", level: 1 },
  { id: "Non- Stockable", label: "Non- Stockable", level: 1 },
  { id: "Return", label: "Return", level: 1 },
  { id: "Stock Adjustment", label: "Stock Adjustment", level: 1 },
  { id: "Purchase Request", label: "Purchase Request", level: 1 },
  
  //   Inv Report sub-items
  { id: "Inv Report", label: "Inv Report", level: 1, isParent: true },
  { id: "Balance Report", label: "Balance Report", level: 2 },
  { id: "Saved Report", label: "Saved Report", level: 2 },
  { id: "GRV Report", label: "GRV Report", level: 2 }, 
  { id: "SIV Report", label: "SIV Report", level: 2 },
  { id: "Non- Stockable Report", label: "Non- Stockable Report", level: 2 },
  { id: "Return Report", label: "Return Report", level: 2 },
  { id: "Adjustment Report", label: "Adjustment Report", level: 2 },
  { id: "Purchase Report", label: "Purchase Report", level: 2 },
  { id: "Store Request Report", label: "Store Request Report", level: 2 },

  // ── Finance ──────────────────────────────────────────
  { id: "Finance", label: "Finance", level: 0, isParent: true },
  { id: "Finance Register", label: "Finance Register", level: 1, isParent: true },
  { id: "Company Profile", label: "Company Profile", level: 2 },
  { id: "Income Category", label: "Income Category", level: 2 },
 // { id: "Cost Category", label: "Cost Category", level: 2 },
  { id: "Chart of Account", label: "Chart of Account", level: 2 },
  { id: "Items Classify", label: "Items Classify", level: 2 },

  { id: "Advanced Register", label: "Advanced Register", level: 1, isParent: true },
  { id: "Income Tax", label: "Income Tax", level: 2 },
  { id: "Pension", label: "Pension", level: 2 },
  { id: "Withholding", label: "Withholding", level: 2 },
  // { id: "Overtime", label: "Overtime", level: 2 },
  // { id: "Earn And Deduct Type", label: "Earn And Deduct Type", level: 2 },
  { id: "Bank Register", label: "Bank Register", level: 2 },

  { id: "Data Posting", label: "Data Posting", level: 1, isParent: true },
  { id: "Income", label: "Income", level: 2 },
  { id: "Withholding Received", label: "Withholding Received", level: 2 },
  { id: "Withholding Issue", label: "Withholding Issue", level: 2 },
  { id: "Payment/ Purchase", label: "Payment/ Purchase", level: 2 },
  { id: "Finance Inventory", label: "Finance Inventory", level: 2 },
  { id: "Bank Reconciliation", label: "Bank Reconciliation", level: 2 },


  { id: "Payroll", label: "Payroll", level: 1, isParent: true },
  // { id: "Monthly Configuration", label: "Monthly Configuration", level: 2,isParent: true  },
  // { id: "Earn And Deduct", label: "Earn And Deduct", level: 3 },
  // { id: "Attendance", label: "Attendance", level: 3 },
  { id: "Payroll List", label: "Payroll List", level: 2 },

  { id: "Finance Report", label: "Finance Report", level: 1, isParent: true },
  { id: "Profit and Loss", label: "Profit and Loss", level: 2 },
  { id: "V.A.T", label: "V.A.T", level: 2 },
  { id: "Purchase Declaration", label: "Purchase Declaration", level: 2 },
  { id: "Sales Declaration", label: "Sales Declaration", level: 2 },
  { id: "Payroll Report", label: "Payroll Report", level: 2 },
  { id: "Bank Reconciliation Report", label: "Bank Reconciliation Report", level: 2 },
  { id: "Receivables", label: "Receivables", level: 2 },
  { id: "Liability", label: "Liability", level: 2 },



    // ── Debones ──────────────────────────────────────
    { id: "Debones", label: "Debones", level: 0, isParent: true },
  
  { id: "Debone Percentage", label: "Debone Percentage", level: 1 },
  { id: "Debone Cost", label: "Debone Cost", level: 1 },
  // { id: "Debone Inventory", label: "Debone Inventory", level: 1 },
  // { id: "Debone Controls", label: "Debone Controls", level: 1 },
  // { id: "Debone Report", label: "Debone Report", level: 1 },

      // ── Cnt. Kitchen ──────────────────────────────────────
  { id: "Cnt. Kitchen", label: "Cnt. Kitchen", level: 0, isParent: true },
  
  { id: "Central Departments", label: "Central Department", level: 1 },
  { id: "Central Products", label: "Central Products", level: 1 },
  { id: "Central Inventory", label: "Central Inventory", level: 1 },
  { id: "Product Distribute", label: "Product Distribute", level: 1 },
  { id: "Item Controls", label: "Item Controls", level: 1 },

  // ── Production ──────────────────────────────────────
  { id: "Production", label: "Production", level: 0, isParent: true },
  
  { id: "Register", label: "Register", level: 1, isParent: true },
  { id: "Choose Ingredient", label: "Choose Ingredient", level: 2 },
  // { id: "Register Debones", label: "Register Debones", level: 2 },
  { id: "Product", label: "Product", level: 2 },
  { id: "Recipe", label: "Recipe", level: 2 },

  { id: "Posting", label: "Posting", level: 1, isParent: true },
  { id: "Daily Inventory", label: "Daily Inventory", level: 2 },
  { id: "Daily Products", label: "Daily Products", level: 2 },
  { id: "Daily Transaction", label: "Daily Transaction", level: 2 },
  { id: "Consumption", label: "Consumption", level: 1 },
  
  
  { id: "Edit Access", label: "Edit Access", level: 1 },
  { id: "GRV Edit Access", label: "GRV Edit Access", level: 2 }, 
  { id: "SIV Edit Access", label: "SIV Edit Access", level: 2 },
  { id: "Non- Stockable Edit Access", label: "Non- Stockable Edit Access", level: 2 },
  { id: "Daily Inventory Edit Access", label: "Daily Inventory Edit Access", level: 2 },
  { id: "Daily Products Edit Access", label: "Daily Products Edit Access", level: 2 },
  { id: "Daily Transaction Edit Access", label: "Daily Transaction Edit Access", level: 2 },
  { id: "Consumption Edit Access", label: "Consumption Edit Access", level: 2 },
  {id:  "Material Inventory Edit Access", label: "Material Inventory Edit Access", level: 2 }, 


  { id: "Material Inventory", label: "Material Inventory", level: 1 },

  { id: "Report", label: "Report", level: 1, isParent: true },
  { id: "Cost", label: "Cost", level: 2 },
  { id: "Transaction", label: "Transaction", level: 2 },
  { id: "Product Report", label: "Product Report", level: 2 },
  // { id: "Deboning", label: "Deboning", level: 2 },
  { id: "Consumption Report", label: "Consumption Report", level: 2 },
  { id: "Menu Engineering", label: "Menu Engineering", level: 2 },
  { id: "Gross Profit", label: "Gross Profit", level: 2 },

  // ── POS ─────────────────────────────────────────────
  { id: "POS", label: "POS", level: 0, isParent: true },
  { id: "Orders", label: "Orders", level: 1 },
  { id: "Special Orders", label: "Special Orders", level: 1 },
  { id: "Management Order", label: "Management Order", level: 1 },
  { id: "Display", label: "Display", level: 1},
  { id: "Sales Report", label: "Sales Report", level: 1 },
  { id: "Special O. Report", label: "Special O. Report", level: 1 },
  { id: "Void Report", label: "Void Report", level: 1 },
  { id: "Transfer Report", label: "Transfer Report", level: 1 },

  // ── HR ──────────────────────────────────────────────
  { id: "HR", label: "HR", level: 0, isParent: true },

  { id: "HR Register", label: "HR Register", level: 1, isParent: true },
  // { id: "HR Company Profile", label: "HR Company Profile", level: 2 },
  { id: "HR Leaving Type", label: "HR Leaving Type", level: 2 },
  { id: "HR Performance Criteria", label: "HR Performance Criteria", level: 2 },
 
 
  { id: "HR Advanced Register", label: "HR Advanced Register", level: 1, isParent: true },
  { id: "HR Work/Shift Time", label: "HR Work/Shift Time", level: 2 },
  { id: "HR Overtime Type", label: "HR Overtime Type", level: 2 },
  { id: "HR Earn And Deduct", label: "HR Earn And Deduct", level: 2 },
  { id: "HR Pension And TAX", label: "HR Pension And TAX", level: 2 },


  { id: "HR Employee", label: "HR Employee", level: 1, isParent: true },
  { id: "HR Find Employee", label: "HR Find Employee", level: 2 },
  { id: "HR Leaving Request", label: "HR Leaving Request", level: 2 },
  { id: "HR Employee Evaluation", label: "HR Employee Evaluation", level: 2 },
  { id: "HR Employee Letter", label: "HR Employee Letter", level: 2 },

  { id: "HR Attendance", label: "HR Attendance", level: 1, isParent: true },
  { id: "HR Attendance Configuration", label: "HR Attendance Configuration", level: 2, isParent: true },
  { id: "HR Earn And Deduct", label: "HR Earn And Deduct", level: 3 },
  { id: "HR Attendance Configuration", label: "HR Attendance", level: 3 },
  { id: "HR Device Attendance", label: "HR Device Attendance", level: 2 },

  { id: "HR Payroll Approval", label: "HR Payroll Approval", level: 1 },

  { id: "HR Report", label: "HR Report", level: 1, isParent: true },
  { id: "HR Payroll Report", label: "HR Payroll Report", level: 2 },
  { id: "HR Leaving Request Report", label: "HR Leaving Request Report", level: 2 },
  { id: "HR Evaluation Report", label: "HR Evaluation Report", level: 2 },
  { id: "HR Letter Report", label: "HR Letter Report", level: 2 },
 
  // ── Settings ────────────────────────────────────────
  { id: "Settings", label: "Settings", level: 0, isParent: true },
    { id: "System Setting", label: "System Setting", level: 1},
  { id: "Clear Data", label: "Clear Data", level: 1 },
  { id: "Payment Methods", label: "Payment Methods", level: 1 },
  { id: "Delivery Company", label: "Delivery Company", level: 1 },
  { id: "QR Generate", label: "QR Generate", level: 1 },
  { id: "Password Reset", label: "Password Reset", level: 1 },
  { id: "Company Constants", label: "Company Constants", level: 1 },
  { id: "Change Server Date", label: "Change Server Date", level: 1 },
]

const privilegeDisplayNumberMap = computed(() => {
  const map = {}
  const counters = {}
  availablePrivileges.forEach((priv) => {
    const level = priv.level
    counters[level] = (counters[level] || 0) + 1
    Object.keys(counters).forEach((key) => {
      if (Number(key) > level) delete counters[key]
    })
    const parts = []
    for (let l = 0; l <= level; l++) {
      parts.push(counters[l] || 1)
    }
    map[priv.id] = parts.join('.')
  })
  return map
})

function getPrivilegeDisplayNumber(priv) {
  return privilegeDisplayNumberMap.value[priv.id] || ''
}

const POS_VIEW_ONLY_REPORT_PRIVILEGES = ['Sales Report', 'Void Report', 'Transfer Report']
const PRIVILEGE_ACTIONS = ['Add', 'Edit', 'View', 'Void', 'Approve']
const HR_EMPLOYEE_PRIVILEGE_ACTIONS = {
  'HR Find Employee': ['Find', 'Edit', 'Detail', 'Print ID', 'Approve'],
  'HR Leaving Request': ['Find', 'Edit', 'Request', 'Approve', null],
  'HR Employee Evaluation': ['Find', 'Edit', 'Evaluation', 'Approve', null],
  'HR Employee Letter': ['Find', 'Edit', 'Request', 'Approve', null],
  'HR Device Attendance': ['Find', 'Save', null, null, null]
}
const HR_REPORT_VIEW_ONLY_PRIVILEGES = new Set([
  'HR Report',
  'HR Payroll Report',
  'HR Leaving Request Report',
  'HR Evaluation Report',
  'HR Letter Report',
])
const HR_REPORT_ACTION_DISPLAY_PRIVILEGES = new Set([
  'HR Report',
  'HR Payroll Report',
  'HR Leaving Request Report',
  'HR Evaluation Report',
  'HR Letter Report',
])
const PAYROLL_WORKFLOW_PRIVILEGES = new Set([
  'Earn And Deduct',
  'Attendance',
  'Payroll List',
  'HR Earn And Deduct',
  'HR Attendance Configuration',
  'HR Payroll Approval'
])
const PAYROLL_WORKFLOW_WITH_EDIT_PRIVILEGES = new Set([
  'Payroll List',
  'HR Payroll Approval'
])
const PAYROLL_WORKFLOW_ACTIONS = ['Prepared By', 'Checked By', 'Cross Checked By', 'Approved By']
const PAYROLL_WORKFLOW_LEGACY_ACTION_MAP = {
  Add: 'Prepared By',
  Edit: 'Checked By',
  View: 'Cross Checked By',
  Approve: 'Approved By'
}

const getInitialPrivileges = () => {
  const privs = {}
  availablePrivileges.forEach(priv => {
    if (PAYROLL_WORKFLOW_PRIVILEGES.has(priv.id)) {
      const workflowPrivs = {
        'Prepared By': false,
        'Checked By': false,
        'Cross Checked By': false,
        'Approved By': false
      }
      privs[priv.id] = PAYROLL_WORKFLOW_WITH_EDIT_PRIVILEGES.has(priv.id)
        ? { Edit: false, ...workflowPrivs }
        : workflowPrivs
    } else if (HR_EMPLOYEE_PRIVILEGE_ACTIONS[priv.id]) {
      privs[priv.id] = Object.fromEntries(
        HR_EMPLOYEE_PRIVILEGE_ACTIONS[priv.id].filter(Boolean).map(action => [action, false])
      )
    } else if (priv.id === 'Menu List') {
      privs[priv.id] = { Add: false, Edit: false, View: false, Available: false, Approve: false }
    } else {
      privs[priv.id] = { Add: false, Edit: false, View: false, Void: false, Approve: false }
    }
  })
  return privs
}

const getInitialForm = () => ({
  full_name: '',
  phone_number: '',
  sex: '',
  username: '',
  salary: null,
  role: '',
  departments: '',
  branch_ids: '',
  departmentId: null,
  category_ids: '',
  privileges: getInitialPrivileges(),
  status: 1,
  password: ''
})

const form = reactive(getInitialForm())
const activeDropdown = ref(null)
const departmentDropdownTrigger = ref(null)
const departmentDropdownStyle = ref({})
const departmentDropdownMaxHeight = ref('16rem')
const categoryDropdownTrigger = ref(null)
const categoryDropdownStyle = ref({})
const categoryDropdownMaxHeight = ref('16rem')
const roleDropdownTrigger = ref(null)
const roleDropdownStyle = ref({})
const roleDropdownMaxHeight = ref('16rem')
const branchDropdownTrigger = ref(null)
const branchDropdownStyle = ref({})
const branchDropdownMaxHeight = ref('16rem')
const selectedRoleIds = ref([])
const selectedDepartmentIds = ref([])
const selectedCategoryIds = ref([])
const selectedBranchIds = ref([])
const isCompanySelected = ref(false)
const privilegeParentMap = buildPrivilegeParentMap()

function isProductionReportSubtree(privId) {
  if (privId === 'Report' && privilegeParentMap[privId] === 'Production') return true
  if (privilegeParentMap[privId] === 'Report' && privilegeParentMap.Report === 'Production') return true
  return false
}

function isViewOnlyPrivilege(privId) {
  if (privId === 'Dashboard' || privilegeParentMap[privId] === 'Dashboard') return true
  if (privId === 'Inv Report' || privilegeParentMap[privId] === 'Inv Report') return true
  if (privId === 'Finance Report' || privilegeParentMap[privId] === 'Finance Report') return true
  if (privId === 'HR Report' || privilegeParentMap[privId] === 'HR Report') return true
  if (isProductionReportSubtree(privId)) return true
  return false
}

function isVoidPrivilegeEditable(privId) {
  return privId === 'Orders' || privId === 'Special Orders' || privId === 'Display'
}

function isAvailablePrivilegeEditable(privId) {
  return privId === 'Menu List'
}

function isPayrollWorkflowPrivilege(privId) {
  return PAYROLL_WORKFLOW_PRIVILEGES.has(privId)
}

function isPayrollWorkflowWithEditPrivilege(privId) {
  return PAYROLL_WORKFLOW_WITH_EDIT_PRIVILEGES.has(privId)
}

function getPrivilegeActionsForPriv(privId) {
  if (isPayrollWorkflowPrivilege(privId)) {
    return isPayrollWorkflowWithEditPrivilege(privId)
      ? ['Edit', ...PAYROLL_WORKFLOW_ACTIONS]
      : PAYROLL_WORKFLOW_ACTIONS
  }
  if (privId === 'Menu List') return ['Add', 'Edit', 'View', 'Available', 'Approve']
  if (HR_REPORT_VIEW_ONLY_PRIVILEGES.has(privId)) return ['View']
  if (HR_EMPLOYEE_PRIVILEGE_ACTIONS[privId]) return HR_EMPLOYEE_PRIVILEGE_ACTIONS[privId].filter(Boolean)
  return PRIVILEGE_ACTIONS
}

function getPrivilegeTableColumnAction(privId, column) {
  if (isPayrollWorkflowPrivilege(privId)) {
    const payrollColumnMap = {
      create: 'Prepared By',
      edit: 'Checked By',
      view: 'Cross Checked By',
      void: null,
      approve: 'Approved By'
    }
    return payrollColumnMap[column] ?? null
  }
  if (HR_EMPLOYEE_PRIVILEGE_ACTIONS[privId]) {
    const hrActions = HR_EMPLOYEE_PRIVILEGE_ACTIONS[privId]
    return hrActions[{ create: 0, edit: 1, view: 2, void: 3, approve: 4 }[column]] ?? null
  }
  if (HR_REPORT_VIEW_ONLY_PRIVILEGES.has(privId)) {
    return column === 'view' ? 'View' : null
  }
  if (column === 'void') return getVoidColumnAction(privId)
  const standardColumnMap = {
    create: 'Add',
    edit: 'Edit',
    view: 'View',
    approve: 'Approve'
  }
  return standardColumnMap[column] ?? null
}

function isPrivilegeColumnVisible(privId, column) {
  if (isPayrollWorkflowPrivilege(privId) && column === 'void') return false
  if (HR_EMPLOYEE_PRIVILEGE_ACTIONS[privId]) return getPrivilegeTableColumnAction(privId, column) !== null
  if (HR_REPORT_VIEW_ONLY_PRIVILEGES.has(privId)) {
    return column === 'view' || (
      HR_REPORT_ACTION_DISPLAY_PRIVILEGES.has(privId) &&
      ['create', 'edit', 'void', 'approve'].includes(column)
    )
  }
  return true
}

function getPrivilegeColumnLabel(privId, column) {
  const action = getPrivilegeTableColumnAction(privId, column)
  if (isPayrollWorkflowPrivilege(privId)) return action || ''
  if (HR_EMPLOYEE_PRIVILEGE_ACTIONS[privId]) return action || ''
  if (HR_REPORT_VIEW_ONLY_PRIVILEGES.has(privId)) {
    if (column === 'view') return 'See'
    if (HR_REPORT_ACTION_DISPLAY_PRIVILEGES.has(privId)) {
      return { create: 'Create', edit: 'Edit', void: 'Void', approve: 'Approve' }[column] || ''
    }
    return ''
  }
  if (column === 'view') return privId === 'Display' ? 'Start' : 'See'
  if (column === 'void') {
    return privId === 'Display' ? 'Finish' : (privId === 'Menu List' ? 'Available' : 'Void')
  }
  if (column === 'approve') {
    return privId === 'Display' ? 'Out' : ((privId === 'Orders' || privId === 'Special Orders') ? 'Confirm' : 'Approve')
  }
  if (column === 'create') return 'Create'
  if (column === 'edit') return 'Edit'
  return ''
}

function migratePayrollWorkflowPrivileges(privileges) {
  if (!privileges || typeof privileges !== 'object') return
  for (const privId of PAYROLL_WORKFLOW_PRIVILEGES) {
    const block = privileges[privId]
    if (!block || typeof block !== 'object') continue
    const keepEdit = isPayrollWorkflowWithEditPrivilege(privId)
    for (const [legacyAction, workflowAction] of Object.entries(PAYROLL_WORKFLOW_LEGACY_ACTION_MAP)) {
      if (keepEdit && legacyAction === 'Edit') continue
      if (block[legacyAction] && !block[workflowAction]) {
        block[workflowAction] = block[legacyAction]
      }
      delete block[legacyAction]
    }
    delete block.Void
    for (const action of getPrivilegeActionsForPriv(privId)) {
      if (block[action] === undefined) block[action] = false
    }
  }
}

function getVoidColumnAction(privId) {
  return privId === 'Menu List' ? 'Available' : 'Void'
}

function isApprovePrivilegeEditable(privId) {
  return (
    privId === 'Store Request' ||
    privId === 'Return' ||
    privId === 'Purchase Request' ||
    privId === 'Orders' ||
    privId === 'Special Orders' ||
    privId === 'Display' ||
    privId === 'SIV (Internal Issue)' ||
    privId === 'HR Leaving Request' ||
    privId === 'HR Employee Evaluation' ||
    privId === 'HR Employee Letter'
  )
}

function isRegisterBranchPrivilege(privId) {
  return privId === 'Branch' && privilegeParentMap[privId] === 'Register Users'
}

const SETTINGS_ONLY_EDIT_PRIVILEGES = new Set([
  'System Setting',
  'Clear Data',
  'Password Reset',
  'Company Constants',
])

function isSettingsChildOnlyEdit(privId) {
  return SETTINGS_ONLY_EDIT_PRIVILEGES.has(privId) && privilegeParentMap[privId] === 'Settings'
}

function isSettingsChildOnlyAdd(privId) {
  return privId === 'QR Generate' && privilegeParentMap[privId] === 'Settings'
}

function isPrivilegeActionLocked(privId, action) {
  if (isPayrollWorkflowPrivilege(privId)) {
    return !getPrivilegeActionsForPriv(privId).includes(action)
  }
  if (isViewOnlyPrivilege(privId) && action !== 'View') return true
  if (privId === 'HR Find Employee' && action === 'Approve') return true
  if (privId === 'Display' && (action === 'Add' || action === 'Edit')) return true
  if (POS_VIEW_ONLY_REPORT_PRIVILEGES.includes(privId) && action !== 'View') return true
  if (isRegisterBranchPrivilege(privId) && (action === 'Add' || action === 'Edit')) return true
  if (isSettingsChildOnlyEdit(privId) && action !== 'Edit') return true
  if (isSettingsChildOnlyAdd(privId) && action !== 'Add') return true
  if (privId === 'Menu List' && action === 'Void') return true
  if (action === 'Available' && !isAvailablePrivilegeEditable(privId)) return true
  if (action === 'Void' && !isVoidPrivilegeEditable(privId)) return true
  if (action === 'Approve' && !isApprovePrivilegeEditable(privId)) return true
  return false
}

function getEditableActionsForPriv(privId) {
  return getPrivilegeActionsForPriv(privId).filter(action => !isPrivilegeActionLocked(privId, action))
}

function enforceLockedPrivileges(privId) {
  if (!form.privileges[privId]) return
  if (isPayrollWorkflowPrivilege(privId)) {
    const cleaned = {}
    for (const action of getPrivilegeActionsForPriv(privId)) {
      cleaned[action] = !!form.privileges[privId][action]
    }
    form.privileges[privId] = cleaned
    return
  }
  for (const action of getPrivilegeActionsForPriv(privId)) {
    if (isPrivilegeActionLocked(privId, action)) {
      form.privileges[privId][action] = false
    }
  }
  if (privId === 'Menu List') {
    form.privileges[privId].Void = false
  }
}

function enforceAllLockedPrivileges() {
  availablePrivileges.forEach((priv) => enforceLockedPrivileges(priv.id))
}

function sanitizeLockedPrivilegesObject(privileges) {
  if (!privileges || typeof privileges !== 'object') return privileges
  migratePayrollWorkflowPrivileges(privileges)
  const out = {}
  for (const [key, actions] of Object.entries(privileges)) {
    out[key] = { ...(actions || {}) }
    if (isPayrollWorkflowPrivilege(key)) {
      const cleaned = {}
      for (const action of getPrivilegeActionsForPriv(key)) {
        cleaned[action] = !!out[key][action]
      }
      out[key] = cleaned
      continue
    }
    for (const action of getPrivilegeActionsForPriv(key)) {
      if (isPrivilegeActionLocked(key, action)) out[key][action] = false
    }
    if (key === 'Menu List') {
      out[key].Void = false
    }
  }
  return out
}

function privilegeCheckboxChecked(privId, action) {
  if (isPrivilegeActionLocked(privId, action)) return false
  return !!form.privileges[privId]?.[action]
}

function getPrivilegeActionSummaryLabel(privId, action) {
  if (isPayrollWorkflowPrivilege(privId)) return action
  return action
}
const currentBranchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || null)
const currentCompanyId = computed(() => currentUser.value?.companyId || currentUser.value?.company?.id || null)
const currentCompanyName = computed(() => currentUser.value?.company?.name || currentUser.value?.companyName || currentUser.value?.company_name || '')
const showBranchSelector = computed(() => !currentBranchId.value && !!currentCompanyId.value)

const selectedRoleNames = computed(() => {
  if (!selectedRoleIds.value.length || !roles.value.length) return []
  const idSet = new Set(selectedRoleIds.value)
  return roles.value.filter(r => idSet.has(r.id)).map(r => r.name)
})

const selectedDepartmentNames = computed(() => {
  if (!selectedDepartmentIds.value.length || !departments.value.length) return []
  const idSet = new Set(selectedDepartmentIds.value)
  return departments.value.filter(d => idSet.has(d.id)).map(d => d.name)
})

const selectedCategoryNames = computed(() => {
  if (!selectedCategoryIds.value.length || !categories.value.length) return []
  const idSet = new Set(selectedCategoryIds.value)
  return categories.value.filter(c => idSet.has(c.id)).map(c => c.name)
})

const selectedBranchNames = computed(() => {
  if (!selectedBranchIds.value.length || !branches.value.length) return []
  const idSet = new Set(selectedBranchIds.value.map(v => Number(v)))
  return branches.value.filter(b => idSet.has(Number(b.id))).map(b => b.name)
})

const selectedOrganizationNames = computed(() => {
  const names = []
  if (isCompanySelected.value && currentCompanyName.value) {
    names.push(`${currentCompanyName.value} (Head Office)`)
  }
  return names.concat(selectedBranchNames.value)
})

function syncSelectedFieldsFromUser(user) {
  // Sync Roles
  if (!user || !user.role || !roles.value.length) {
    selectedRoleIds.value = []
  } else {
    const roleNames = user.role.toString().split(',').map(s => s.trim()).filter(Boolean)
    const nameToRoleId = new Map(roles.value.map(r => [r.name, r.id]))
    selectedRoleIds.value = roleNames.map(name => nameToRoleId.get(name)).filter(id => id != null)
  }

  // Sync Departments
  if (!user || !user.departments || !departments.value.length) {
    selectedDepartmentIds.value = []
    form.departmentId = user?.departmentId || null
  } else {
    const deptNames = user.departments.toString().split(',').map(s => s.trim()).filter(Boolean)
    const nameToDeptId = new Map(departments.value.map(d => [d.name, d.id]))
    selectedDepartmentIds.value = deptNames.map(name => nameToDeptId.get(name)).filter(id => id != null)
    form.departmentId = selectedDepartmentIds.value.length ? selectedDepartmentIds.value[0] : null
  }

  // Sync Store Categories (comma-separated IDs)
  const categoryIdList = user?.category_ids
    ? user.category_ids.toString().split(',').map(v => Number(v.trim())).filter(v => Number.isInteger(v) && v > 0)
    : []
  selectedCategoryIds.value = categoryIdList
  form.category_ids = categoryIdList.length ? categoryIdList.join(',') : ''

  // Sync Branches for company-level users
  const branchIdList = user?.branch_ids
    ? user.branch_ids.toString().split(',').map(v => Number(v.trim())).filter(v => Number.isInteger(v) && v > 0)
    : (user?.branchId ? [Number(user.branchId)] : [])
  selectedBranchIds.value = showBranchSelector.value ? branchIdList : []

  const companyIdList = user?.company_assigned_ids
    ? user.company_assigned_ids.toString().split(',').map(v => Number(v.trim())).filter(v => Number.isInteger(v) && v > 0)
    : []
  isCompanySelected.value = !!(showBranchSelector.value && currentCompanyId.value && companyIdList.includes(Number(currentCompanyId.value)))
}

// Load departments and roles from database
async function loadRolesAndDepartments() {
  if (!currentUser.value) return
  
  try {
    const branchId = currentBranchId.value
    const companyId = currentCompanyId.value

    const params = new URLSearchParams()
    if (branchId) params.append('branchId', branchId)
    else if (companyId) params.append('companyId', companyId)

    if (params.toString()) {
      const deptRes = await apiFetch(`/departments?${params.toString()}`)
      
      if (deptRes.ok) departments.value = await deptRes.json()
      departments.value = departments.value.filter(d => d.status === 1);
      // Note: adjust the role fetch logic based on actual role endpoints. Let's do them sequential to be safe.
      departments.value.sort((a, b) => a.name.localeCompare(b.name))

      const catRes = await apiFetch(`/categories?${params.toString()}`)
      if (catRes.ok) categories.value = await catRes.json()
      categories.value = (Array.isArray(categories.value) ? categories.value : []).filter(c => c.status === 1)
      categories.value.sort((a, b) => a.name.localeCompare(b.name))

      let roleUrl = ''
      if (branchId) roleUrl = `/roles/branch/${branchId}`
      else if (companyId) roleUrl = `/roles/company/${companyId}`
      if (roleUrl) {
         const rRes = await apiFetch(roleUrl)
         if (rRes.ok) roles.value = await rRes.json()
         roles.value = roles.value.filter(r => r.status === 1);
         roles.value.sort((a, b) => a.name.localeCompare(b.name))
      }

      if (showBranchSelector.value && companyId) {
        const branchRes = await apiFetch(`/branches/company/${companyId}`)
        if (branchRes.ok) {
          const allBranches = await branchRes.json()
          branches.value = (Array.isArray(allBranches) ? allBranches : []).filter(b => b.is_active === 1)
          branches.value.sort((a, b) => a.name.localeCompare(b.name))
        } else {
          branches.value = []
        }
      } else {
        branches.value = []
      }

      // if editing, align selected roles/deps once available
      if (props.editUser) {
        syncSelectedFieldsFromUser(props.editUser)
      }
    }
  } catch (error) {
    console.error('Error loading departments:', error)
  }
}

// Pre-fill form when editing an existing user
watch(() => props.editUser, (user) => {
  if (user) {
    const rawPrivs = user.privilege 
      ? (typeof user.privilege === 'string' ? JSON.parse(user.privilege) : JSON.parse(JSON.stringify(user.privilege)))
      : {}
    
    // Deep merge to ensure all categories have Add/Edit/View properties
    const initialPrivs = getInitialPrivileges()
    const mergedPrivs = {}
    for (const key in initialPrivs) {
      mergedPrivs[key] = {
        ...initialPrivs[key],
        ...(rawPrivs[key] || {})
      }
      if (key === 'Menu List') {
        if (mergedPrivs[key].Available === undefined) {
          mergedPrivs[key].Available = !!mergedPrivs[key].Void
        }
        mergedPrivs[key].Void = false
        if (mergedPrivs[key].Available === undefined) mergedPrivs[key].Available = false
      } else if (mergedPrivs[key].Void === undefined) {
        mergedPrivs[key].Void = false
      }
      if (mergedPrivs[key].Approve === undefined) mergedPrivs[key].Approve = false
    }
    migratePayrollWorkflowPrivileges(mergedPrivs)

    Object.assign(form, {
      full_name: user.full_name || '',
      phone_number: user.phone_number || '',
      sex: user.sex || '',
      username: user.username || '',
      salary: user.salary ?? null,
      role: user.role || '',
      departments: user.departments || '',
      departmentId: user.departmentId || null,
      category_ids: user.category_ids || '',
      privileges: sanitizeLockedPrivilegesObject(mergedPrivs),
      status: user.status ?? 1,
      password: '' // Don't pre-fill password
    })

    // keep dropdowns in sync when editing
    syncSelectedFieldsFromUser(user)
  } else {
    Object.assign(form, getInitialForm())
    selectedRoleIds.value = []
    selectedDepartmentIds.value = []
    selectedCategoryIds.value = []
    selectedBranchIds.value = []
    isCompanySelected.value = false
  }
}, { immediate: true })

function toggleAll(priv) {
  const current = isAllChecked(priv)
  const next = !current
  for (const action of getPrivilegeActionsForPriv(priv)) {
    if (isPrivilegeActionLocked(priv, action)) {
      form.privileges[priv][action] = false
    } else {
      form.privileges[priv][action] = next
    }
  }
  if (priv === 'Menu List') {
    form.privileges[priv].Void = false
  }
  if (next) ensureParentChecked(priv)
}

function isAllChecked(priv) {
  const p = form.privileges[priv]
  if (!p) return false
  const editable = getEditableActionsForPriv(priv)
  if (!editable.length) return false
  return editable.every(action => !!p[action])
}

function hasAnyPrivilegeChecked(priv) {
  const p = form.privileges[priv]
  if (!p) return false
  return getEditableActionsForPriv(priv).some(action => !!p[action])
}

function ensureParentChecked(priv) {
  let current = priv
  while (privilegeParentMap[current]) {
    const parent = privilegeParentMap[current]
    if (!hasAnyPrivilegeChecked(parent)) {
      form.privileges[parent].Add = true
      form.privileges[parent].Edit = true
      form.privileges[parent].View = true
      form.privileges[parent].Void = true
      form.privileges[parent].Approve = true
      current = parent
    } else {
      break
    }
  }
}

function togglePrivilegeAction(priv, action, checked) {
  if (isPrivilegeActionLocked(priv, action)) {
    form.privileges[priv][action] = false
    return
  }
  form.privileges[priv][action] = checked
  if (checked) ensureParentChecked(priv)
}

function buildPrivilegeParentMap() {
  const map = {}
  const parentAtLevel = {}
  availablePrivileges.forEach((priv) => {
    parentAtLevel[priv.level] = priv.id
    const parentId = parentAtLevel[priv.level - 1]
    if (priv.level > 0 && parentId) {
      map[priv.id] = parentId
    }
    Object.keys(parentAtLevel).forEach((levelKey) => {
      if (Number(levelKey) > priv.level) {
        delete parentAtLevel[levelKey]
      }
    })
  })
  return map
}

const getSelectedPrivilegesCount = computed(() => {
  let count = 0
  for (const priv in form.privileges) {
    if (form.privileges[priv] && Object.values(form.privileges[priv]).some(v => v)) count++
  }
  return count
})

const getSelectedPrivilegesSummary = computed(() => {
  const summary = []
  for (const priv in form.privileges) {
    if (form.privileges[priv] && typeof form.privileges[priv] === 'object') {
      const actions = Object.entries(form.privileges[priv])
        .filter(([, val]) => val)
        .map(([action]) => getPrivilegeActionSummaryLabel(priv, action))
      if (actions.length > 0) {
        summary.push(`${priv} (${actions.join('/')})`)
      }
    }
  }
  return summary.join(', ')
})

function updateFixedDropdownPosition(activeKey, triggerRef, styleRef, maxHeightRef) {
  if (activeDropdown.value !== activeKey) return
  const el = triggerRef.value
  if (!el) return

  const rect = el.getBoundingClientRect()
  const edgeGap = 8
  const panelWidth = rect.width
  let left = rect.left
  if (left + panelWidth > window.innerWidth - edgeGap) {
    left = Math.max(edgeGap, window.innerWidth - panelWidth - edgeGap)
  }

  const spaceBelow = Math.max(120, Math.floor(window.innerHeight - rect.bottom - edgeGap))
  const spaceAbove = Math.max(120, Math.floor(rect.top - edgeGap))
  const openUp = spaceBelow < 260 && spaceAbove > spaceBelow
  const gap = 4

  if (openUp) {
    maxHeightRef.value = `${Math.max(120, spaceAbove - gap)}px`
    styleRef.value = {
      left: `${left}px`,
      width: `${panelWidth}px`,
      bottom: `${window.innerHeight - rect.top + gap}px`,
      top: 'auto',
    }
  } else {
    maxHeightRef.value = `${Math.max(120, spaceBelow - gap)}px`
    styleRef.value = {
      left: `${left}px`,
      width: `${panelWidth}px`,
      top: `${rect.bottom + gap}px`,
      bottom: 'auto',
    }
  }
}

function updateDepartmentDropdownPosition() {
  updateFixedDropdownPosition('departments', departmentDropdownTrigger, departmentDropdownStyle, departmentDropdownMaxHeight)
}

function updateCategoryDropdownPosition() {
  updateFixedDropdownPosition('categories', categoryDropdownTrigger, categoryDropdownStyle, categoryDropdownMaxHeight)
}

function updateRoleDropdownPosition() {
  updateFixedDropdownPosition('roles', roleDropdownTrigger, roleDropdownStyle, roleDropdownMaxHeight)
}

function updateBranchDropdownPosition() {
  updateFixedDropdownPosition('branches', branchDropdownTrigger, branchDropdownStyle, branchDropdownMaxHeight)
}

function updateActiveDropdownPosition() {
  updateDepartmentDropdownPosition()
  updateCategoryDropdownPosition()
  updateRoleDropdownPosition()
  updateBranchDropdownPosition()
}

function toggleDropdown(type) {
  if (activeDropdown.value === type) {
    activeDropdown.value = null
  } else {
    activeDropdown.value = type
    nextTick(() => updateActiveDropdownPosition())
  }
}

function closeModal() {
  Object.assign(form, getInitialForm())
  enforceAllLockedPrivileges()
  activeDropdown.value = null
  selectedRoleIds.value = []
  selectedDepartmentIds.value = []
  selectedCategoryIds.value = []
  selectedBranchIds.value = []
  isCompanySelected.value = false
  saving.value = false
  emit('close')
}

function getSelectedPrivilegesOnly(privileges) {
  const selected = {}
  if (!privileges || typeof privileges !== 'object') return selected
  for (const [key, actions] of Object.entries(privileges)) {
    if (!actions || typeof actions !== 'object') continue
    const cleaned = {}
    for (const action of getPrivilegeActionsForPriv(key)) {
      if (isPrivilegeActionLocked(key, action)) continue
      if (actions[action]) cleaned[action] = true
    }
    if (Object.keys(cleaned).length > 0) {
      selected[key] = cleaned
    }
  }
  return selected
}

async function handleSubmit() {
  const usernameTrimmed = String(form.username || '').trim().toLowerCase()
  if (!usernameTrimmed) {
    alert('Username is required.')
    return
  }

  const hasSelectedBranches = selectedBranchIds.value.length > 0
  const branchPrivilege = form.privileges?.Branch || {}
  const hasAnyBranchPrivilege =
    !!branchPrivilege.Add ||
    !!branchPrivilege.Edit ||
    !!branchPrivilege.View ||
    !!branchPrivilege.Void ||
    !!branchPrivilege.Approve

  if (hasSelectedBranches && !isAdminPrivilege.value && !hasAnyBranchPrivilege) {
    alert('Please select at least one Branch privilege action (Add/Edit/View/Void/Approve) when branch is selected.')
    return
  }

  if (saving.value) return

  saving.value = true
  try {
    const branchId = currentBranchId.value
    const companyId = currentCompanyId.value

    let roleName = ''
    if (isAdminPrivilege.value) {
      roleName = props.editUser.role
    } else {
      const selectedRolesObj = roles.value.filter(r => selectedRoleIds.value.includes(r.id))
      roleName = selectedRolesObj.map(r => r.name).join(',')
    }

    const selectedDepts = departments.value.filter(d => selectedDepartmentIds.value.includes(d.id))
    const departmentsName = selectedDepts.map(d => d.name).join(',')
    const primaryDeptId = selectedDepts[0] ? selectedDepts[0].id : null
    const categoryIdsValue = selectedCategoryIds.value
      .map(v => Number(v))
      .filter(v => Number.isInteger(v) && v > 0)
      .join(',')

    const payload = {
      ...form,
      username: usernameTrimmed,
      role: roleName,
      departments: departmentsName,
      departmentId: primaryDeptId,
      category_ids: categoryIdsValue || null,
      companyId,
      branchId,
      branch_ids: showBranchSelector.value ? selectedBranchIds.value.map(v => Number(v)).filter(v => Number.isInteger(v) && v > 0).join(',') : null,
      company_assigned_ids: showBranchSelector.value && isCompanySelected.value && currentCompanyId.value
        ? String(Number(currentCompanyId.value))
        : null,
      created_by: currentUser.value.id || currentUser.value.sub,
      updated_by: currentUser.value.id || currentUser.value.sub,
      privilege: isAdminPrivilege.value ? props.editUser.privilege : getSelectedPrivilegesOnly(form.privileges)
    }

    if (props.editUser) {
      // Edit mode: handled by parent (usersPage.vue) via handleUpdate
      await emit('update', { ...props.editUser, ...payload })
    } else {
      // Add mode: Submit to API here or emit register
      if (!form.password) {
        // Generate a simple random password if empty
        payload.password = Math.floor(1000 + Math.random() * 9000).toString()
        alert(`User generated password: ${payload.password}`)
      }
      
      const response = await apiFetch('users/register', {
        method: 'POST',
        body: JSON.stringify(payload)
      })

      if (response.ok) {
        const newUser = await response.json()
        emit('register', newUser)
        window.dispatchEvent(new CustomEvent('users-updated'))
        closeModal()
      } else {
        const err = await response.json()
        alert(err.message || 'Registration failed')
      }
    }
  } catch (error) {
    console.error('Error submitting user:', error)
    alert('An error occurred')
  } finally {
    saving.value = false
  }
}

// Click outside to close dropdowns
function handleOutsideClick(e) {
  if (
    !e.target.closest('.dropdown-container') &&
    !e.target.closest('.teleport-dropdown') &&
    !e.target.closest('.department-dropdown-panel') &&
    !e.target.closest('.category-dropdown-panel') &&
    !e.target.closest('.role-dropdown-panel') &&
    !e.target.closest('.branch-dropdown-panel')
  ) {
    activeDropdown.value = null
  }
}

onMounted(() => {
  window.addEventListener('click', handleOutsideClick)
  window.addEventListener('scroll', updateActiveDropdownPosition, true)
  window.addEventListener('resize', updateActiveDropdownPosition)
})

onUnmounted(() => {
  window.removeEventListener('click', handleOutsideClick)
  window.removeEventListener('scroll', updateActiveDropdownPosition, true)
  window.removeEventListener('resize', updateActiveDropdownPosition)
})

watch(activeDropdown, (val) => {
  if (val === 'departments' || val === 'categories' || val === 'roles' || val === 'branches') {
    nextTick(() => updateActiveDropdownPosition())
  }
})

watch(() => props.isOpen, (val) => {
  if (val) loadRolesAndDepartments()
  else saving.value = false
})
</script>

<style scoped>
.animate-in {
  animation: 0.2s ease-out forwards;
}
.fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}
.zoom-in {
  from { transform: scale(0.95); }
  to { transform: scale(1); }
}
.slide-in-from-top-1 {
  from { transform: translateY(-4px); }
  to { transform: translateY(0); }
}

.privilege-payroll-actions {
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}

.privilege-payroll-actions-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  align-items: center;
  justify-items: center;
  width: 100%;
}

</style>
